import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Rating } from "@/lib/study/sm2";

export interface CachedDeck {
  id: string;
  title: string;
  description: string | null;
  cardCount: number;
  cachedAt: number;
}

export interface CachedCard {
  id: string;
  deckId: string;
  question: string;
  answer: string;
  hint: string | null;
  difficulty: "easy" | "medium" | "hard";
  position: number;
  easeFactor: number;
  intervalDays: number;
  repetitions: number;
  nextReviewAt: string;
}

export interface PendingReview {
  id: string;
  deckId: string;
  sessionId: string | null;
  cardId: string;
  rating: Rating;
  timeTakenMs: number;
  createdAt: number;
}

export interface PendingSession {
  id: string;
  deckId: string;
  totalCards: number;
  correctCount: number;
  durationSeconds: number;
  mode: "flashcard" | "mcq";
  reviews: { cardId: string; rating: Rating; timeTakenMs: number }[];
  createdAt: number;
}

interface SnapCardsDB extends DBSchema {
  decks: {
    key: string;
    value: CachedDeck;
    indexes: { "by-cached-at": number };
  };
  cards: {
    key: string;
    value: CachedCard;
    indexes: { "by-deck": string; "by-next-review": string };
  };
  pendingReviews: {
    key: string;
    value: PendingReview;
    indexes: { "by-deck": string };
  };
  pendingSessions: {
    key: string;
    value: PendingSession;
    indexes: { "by-deck": string };
  };
}

const DB_NAME = "snapcards-offline";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<SnapCardsDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<SnapCardsDB>> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("IndexedDB not available on server"));
  }
  if (!dbPromise) {
    dbPromise = openDB<SnapCardsDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const deckStore = db.createObjectStore("decks", { keyPath: "id" });
        deckStore.createIndex("by-cached-at", "cachedAt");

        const cardStore = db.createObjectStore("cards", { keyPath: "id" });
        cardStore.createIndex("by-deck", "deckId");
        cardStore.createIndex("by-next-review", "nextReviewAt");

        const reviewStore = db.createObjectStore("pendingReviews", { keyPath: "id" });
        reviewStore.createIndex("by-deck", "deckId");
        const sessionStore = db.createObjectStore("pendingSessions", { keyPath: "id" });
        sessionStore.createIndex("by-deck", "deckId");
      },
    });
  }
  return dbPromise;
}

export async function cacheDeck(
  deck: { id: string; title: string; description: string | null; card_count: number },
  cards: {
    id: string;
    deck_id: string;
    question: string;
    answer: string;
    hint: string | null;
    difficulty: "easy" | "medium" | "hard";
    position: number;
    ease_factor: number;
    interval_days: number;
    repetitions: number;
    next_review_at: string;
  }[]
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(["decks", "cards"], "readwrite");

  tx.objectStore("decks").put({
    id: deck.id,
    title: deck.title,
    description: deck.description,
    cardCount: deck.card_count,
    cachedAt: Date.now(),
  });

  const cardStore = tx.objectStore("cards");
  for (const card of cards) {
    cardStore.put({
      id: card.id,
      deckId: card.deck_id,
      question: card.question,
      answer: card.answer,
      hint: card.hint,
      difficulty: card.difficulty,
      position: card.position,
      easeFactor: card.ease_factor,
      intervalDays: card.interval_days,
      repetitions: card.repetitions,
      nextReviewAt: card.next_review_at,
    });
  }

  await tx.done;
}

export async function getCachedDeck(deckId: string): Promise<CachedDeck | undefined> {
  const db = await getDB();
  return db.get("decks", deckId);
}

export async function getCachedCards(deckId: string): Promise<CachedCard[]> {
  const db = await getDB();
  return db.getAllFromIndex("cards", "by-deck", deckId);
}

export async function getAllCachedDecks(): Promise<CachedDeck[]> {
  const db = await getDB();
  return db.getAll("decks");
}

export async function removeCachedDeck(deckId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(["decks", "cards"], "readwrite");

  tx.objectStore("decks").delete(deckId);

  const cardKeys = await tx.objectStore("cards").index("by-deck").getAllKeys(deckId);
  for (const key of cardKeys) {
    tx.objectStore("cards").delete(key);
  }

  await tx.done;
}

/** Replace cards in a single transaction — caller supplies fully updated records. */
export async function putCards(cards: CachedCard[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("cards", "readwrite");
  const store = tx.objectStore("cards");
  for (const card of cards) {
    store.put(card);
  }
  await tx.done;
}

export async function addPendingSession(session: PendingSession): Promise<void> {
  const db = await getDB();
  await db.put("pendingSessions", session);
}

export async function getAllPendingSessions(): Promise<PendingSession[]> {
  const db = await getDB();
  return db.getAll("pendingSessions");
}

export async function removePendingSession(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("pendingSessions", id);
}

export async function getPendingSessionCount(): Promise<number> {
  const db = await getDB();
  return db.count("pendingSessions");
}

const MAX_CACHED_DECKS = 50;

/** Evict oldest cached decks if over the limit */
export async function evictOldDecks(): Promise<void> {
  const db = await getDB();
  const allDecks = await db.getAllFromIndex("decks", "by-cached-at");

  if (allDecks.length <= MAX_CACHED_DECKS) return;

  const toEvict = allDecks.slice(0, allDecks.length - MAX_CACHED_DECKS);
  for (const deck of toEvict) {
    await removeCachedDeck(deck.id);
  }
}
